import AppPopup from '@/components/popups/app-popup';
import EventBus from '@/scripts/event-bus';
import BoardUtility from '@/scripts/board-utility';
import { LandingPageService } from '@/scripts/services/landing-page/landing-page-service';
import { LandingPageTemplateService } from '@/scripts/services/landing-page/landing-page-template-service';
import { LogLevel } from "@/scripts/logger";

export const LandingPageTemplateBoardServiceMixin = {
    data() {
        return {
            deletingItems: [],
        };
    },

    computed: {
        isOrganizationContext() {
            return this.$store.getters.isOrganizationContext;
        },

        accountId() {
            return this.$store.state.context.account.id;
        },

        agencyId() {
            return this.$store.state.context.agency.id;
        },

        clientId() {
            return this.$store.state.context.client.id;
        },

        routeBaseUrl() {
            return this.$store.getters.routeBaseUrl;
        },
    },

    methods: {
        refreshEvent() {
            EventBus.$emit('landing-page-template:update');
        },

        createLandingPage(template, hasCategoriesEnabled = true) {
            const widget = {
                name: 'landing-page-settings-widget',
                landingPageTemplateId: template.id,
                hasCategoriesEnabled,
            };

            EventBus.$emit('open-widget', widget);
        },

        addToMyLandingPageTemplates({ id }) {
            LandingPageTemplateService
                .addToMyLandingPageTemplates(this.accountId, id)
                .then(() => {
                    this.$helpers.showSuccessNotification(
                        'You\'ve successfully added the landing page template to My Templates.'
                    );
                    this.refreshEvent();
                })
                .catch((error) => {
                    this.$helpers.showFailNotification(
                        'There was an error adding the landing page template.',
                        { error }
                    );
                })
        },

        duplicateItem({ id, template: { name } }) {
            const widget = {
                name: 'duplicate-widget',
                entityId: id,
                entityName: name,
                serviceName: 'LandingPageTemplates',
                entityType: 'Landing page template',
                fieldLabel: 'Choose a name for the new landing page template',
                fieldPlaceholder: 'Template Name',
                replaceEntityField: 'Name',
                onSuccess: () => {
                    this.refreshEvent();
                },
            };

            EventBus.$emit('open-widget', widget);
        },

        editItem({ id }) {
            const widget = {
                name: 'landing-page-template-widget',
                landingPageTemplateId: id,
            };

            EventBus.$emit('open-widget', widget);
        },

        onPublish(item) {
            if (!item.isAutoPublished) {
                const widget = {
                    name: 'landing-page-template-publish-widget',
                    landingPageTemplateId: item.id,
                };

                EventBus.$emit('open-widget', widget);
            } else {
                this.$helpers.showFailNotification('This template is already set to Auto Publish', { level: LogLevel.INFO });
            }
        },

        onUnpublish({ id }) {
            LandingPageTemplateService.unpublish(id)
                .then(() => {
                    this.$helpers.showSuccessNotification('Your publish settings have been updated.');
                    this.refreshEvent();
                })
                .catch((error) => {
                    this.$helpers.showFailNotification('An error occurred while saving your landing page publish settings.', { error });
                })
        },

        deleteItem(item) {
            this.deletingItems = [item];
            LandingPageTemplateService.delete(item.id)
                .then(() => {
                    this.$helpers.showSuccessNotification('Landing page template deleted successfully.');
                    this.refreshEvent();
                })
                .catch((error) => {
                    this.$helpers.showFailNotification('An error occurred trying to delete the landing page template.', { error });
                    this.deletingItems = [];
                });
        },

        // Factories

        createDeleteLandingPageTemplateModal(item, confirmFunction) {
            return () => {
                const actions = {
                    confirm: {
                        label: 'Delete Landing Page Template',
                        action: () => confirmFunction(item),
                    },

                    cancel: {
                        label: 'Cancel',
                    },
                };

                const content = '<p>All information associated with the landing page template will be deleted. You can not undo this action.</p>';

                this.$modal.show(
                    AppPopup,
                    {
                        type: 'alert-popup',
                        title: 'Are you sure you want to delete this landing page template?',
                        content,
                        actions,
                        confirmText: 'Delete',
                    },
                    {
                        height: this.$helpers.setPopupHeight(),
                        width: this.$helpers.setPopupWidth(),
                    }
                );
            };
        },

        // Table mapper

        landingPageTemplateItems(items) {
            return items.map((item) => {
                const templateItem = {
                    id: item.Id,
                    template: {
                        id: item.Id,
                        name: item.Name,
                        metaTitle: item.MetaTitle,
                        thumbnailImageUrl: item.PreviewImageUrl || null,
                        faviconUrl: item.FaviconUrl,
                        config: item.TemplateJson,
                    },
                    autoPublished: {
                        landingPageTemplateId: item.Id,
                        name: item.Name,
                        isAutoPublished: item.IsAutoPublished,
                        isLoading: false,
                    },
                    asset: {
                        landingPageTemplateId: item.Id,
                        isAutoPublished: item.IsAutoPublished,
                        published: item['SharedAssets@odata.count'],
                    },
                    categories: item.Categories.map(({ Name }) => ({name: Name})),
                    isAutoPublished: item.IsAutoPublished,
                    published: item['SharedAssets@odata.count'],
                    createdAt: this.$moment(item.CreatedAt).local(),
                    publishedAt: this.$moment(item.PublishedAt).local(),
                    updatedAt: this.$moment(item.UpdatedAt).local(),
                };

                if (item.Account) {
                    const { Agency } = item.Account;
                    templateItem.vendor = {
                        id: Agency.Id,
                        link: null,
                        name: Agency.Name,
                        logoUrl: Agency.LogoImageUrl,
                    }
                };

                return templateItem;
            })
        },

        // Query methods
        getFilterParam(options, status) {
            const queryFilters = [];

            if (status != null) {
                const typeFilter = `Status eq TriDigital.EntityDomain.Entities.LandingPageStatus'${status}'`;
                queryFilters.push(typeFilter);
            }

            if (options.search) {
                queryFilters.push(this.getSearchFilter(options.search));
            }

            if (this.accountId) {
                queryFilters.push(`AccountId eq ${this.accountId}`);
            }

            if (this.agencyId) {
                queryFilters.push(`Account/AgencyId eq ${ this.agencyId }`);
            }

            return queryFilters.length > 0 ? `${queryFilters.join(' and ')}` : null;
        },

        getLandingPageTemplatesFilterParam(options, type) {
            const queryFilters = [];

            if (options.filters) {
                const viewQueryFilter = this.getViewQueryFilter(options.filters);
                if (viewQueryFilter) {
                    queryFilters.push(viewQueryFilter);
                }
            }

            if (options.search) {
                queryFilters.push(this.getSearchFilter(options.search));
            };

            if (type === 'shared') {
                queryFilters.push(`SharedAssets/any(a: a/AgencyClient/ClientId eq ${this.clientId})`);
                return queryFilters.length > 0 ? `${queryFilters.join(' and ')}` : '';
            };

            if (type === 'subscription' && options.promoId) {
                queryFilters.push(`SharedAssets/any(a: a/PromoId eq ${options.promoId})`);
            } else {
                queryFilters.push(`AccountId eq ${ this.accountId }`);
            };

            if (type === 'published') {
                const typeFilter = this.isOrganizationContext
                    ? 'SharedAssets/any()'
                    : 'SharedAssets/any(a: a/AgencyClient/IsAccepted eq true)';
                queryFilters.push(typeFilter);
            } else if (type === 'unpublished') {
                queryFilters.push('not SharedAssets/any()');
            };

            if (this.agencyId) {
                queryFilters.push(`Account/AgencyId eq ${ this.agencyId }`);
            }

            return queryFilters.length > 0 ? `${queryFilters.join(' and ')}` : '';
        },

        getViewQueryFilter(filters) {
            if (filters.value.length > 0) {
                const viewQueryFilters = filters.value.map(this.mapFilter);

                if (filters.operation === 'Or') {
                    return `(${viewQueryFilters.join(' or ')})`;
                } else {
                    return `(${viewQueryFilters.join(' and ')})`;
                }
            }

            return null;
        },

        mapFilter(filter) {
            switch (filter.key) {
                case 'name':
                    return BoardUtility.getLandingPageNameFilter(filter);
                case 'categories':
                    return BoardUtility.getLandingPageCategoryFilter(filter);
                default:
                    return null;
            }
        },

        getSearchFilter(search) {
            const encodedSearch = encodeURIComponent(search);

            return `(contains(Name, '${encodedSearch}'))`;
        },

        getPageParam(options) {
            return options.itemsPerPage && options.page ? `${options.itemsPerPage * (options.page - 1)}` : null;
        },

        getTopParam(options) {
            return options.itemsPerPage ? `${options.itemsPerPage}` : null;
        },

        getOrderParam(options) {
            return options.sort ? `${options.sort.property} ${options.sort.direction}` : null;
        },

        getLandingPageTemplateExpandParams(type) {
            const query = ['Categories($select=Name)'];

            if (type === 'shared') {
                query.push('Account($select=Id;$expand=Agency($select=LogoImageUrl,Name,Id))');
            } else if (type === 'published') {
                query.push('SharedAssets($count=true;$top=0)');
            };

            return query.length > 0 ? `${ query.join(',') }` : '';
        },

        createLandingPageParamsFromBoardQuery(options, statusType) {
            return {
                filter: this.getFilterParam(options, statusType),
                skip: this.getPageParam(options),
                top: this.getTopParam(options),
                orderBy: this.getOrderParam(options),
                expand: 'Categories($select=Name)',
                count: true,
            }
        },

        createLandingPageTemplateParamsFromBoardQuery(options, type) {
            return {
                filter: this.getLandingPageTemplatesFilterParam(options, type),
                skip: this.getPageParam(options),
                top: this.getTopParam(options),
                orderBy: this.getOrderParam(options),
                expand: this.getLandingPageTemplateExpandParams(type),
                count: true,
            };
        },

        // Get landing pages
        dispatchGetLandingPages({ options = {}, status = null }) {

            const requestConfig = this.createLandingPageParamsFromBoardQuery(options, status);

            return new Promise((resolve, reject) => {
                LandingPageService.getAll(requestConfig)
                    .then(({ data }) => resolve({
                            items: this.landingPageTemplateItems(data.value),
                            count: data['@odata.count'],
                    }))
                    .catch(reject);
            });
        },

        dispatchGetLandingPageTemplates({ options = {}, type = null }, signal) {
            const requestConfig = this.createLandingPageTemplateParamsFromBoardQuery(options, type);

            return new Promise((resolve, reject) => {
                LandingPageTemplateService.getAll(requestConfig, signal)
                    .then(({ data }) => resolve({
                            items: this.landingPageTemplateItems(data.value),
                            count: data['@odata.count'],
                    }))
                    .catch(reject);
            });
        },
    },
};
